import React from 'react';

import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { TabList } from '@/components/ui/navigation/TabList';
import {
  CreateMonthlyCutGeneralForm,
  CreateMonthlyCutGroupsForm,
} from '@/components/features/monthlyCuts';
import { useCreateMonthlyCut } from '@/hooks/attendance/monthlyCuts';
import type { MonthlyCutTabId } from '@/hooks/attendance/monthlyCuts/useCreateMonthlyCut';
import { useTranslation } from '@/i18n';


export const MonthlyCutCreatePage: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    permissionsLoaded,
    canCreate,
    screenCode,
    isLoading,
    isSubmitting,
    activeTab,
    setActiveTab,
    form,
    companyOptions,
    yearOptions,
    monthOptions,
    groups,
    handleCancel,
    handleCreate,
  } = useCreateMonthlyCut();

  if (permissionsLoaded && !canCreate) return null;

  const groupsErrorMessage = form.errors.groupIds?.message
    ? translate(form.errors.groupIds.message)
    : undefined;

  const tabs = [
    {
      id: 'general',
      label: translate('attendance:monthlyCutCreate.tabs.general'),
      content: (
        <CreateMonthlyCutGeneralForm
          form={form}
          companyOptions={companyOptions}
          yearOptions={yearOptions}
          monthOptions={monthOptions}
        />
      ),
    },
    {
      id: 'agrupacion',
      label: translate('attendance:monthlyCutCreate.tabs.groups'),
      content: (
        <CreateMonthlyCutGroupsForm groups={groups} errorMessage={groupsErrorMessage} />
      ),
    },
  ];

  return (
    <PageLayout isLoading={isLoading} dynamicTitle={translate('attendance:monthlyCutCreate.title')}>
      <div className="flex flex-col gap-6" data-testid="monthly-cuts-create-page">
        <TabList
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as MonthlyCutTabId)}
          keepMounted
        />

        <div className="flex justify-center gap-3 pt-2">
          <PrimaryButton
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
            data-testid="monthly-cut-cancel-button"
          >
            {translate('common:actions.cancel')}
          </PrimaryButton>
          <PermissionGuard action="create" screenCode={screenCode}>
            <PrimaryButton
              onClick={handleCreate}
              isLoading={isSubmitting}
              disabled={isSubmitting}
              data-testid="monthly-cut-save-button"
            >
              {translate('common:actions.save')}
            </PrimaryButton>
          </PermissionGuard>
        </div>
      </div>
    </PageLayout>
  );
};

export default MonthlyCutCreatePage;
